import { motion } from "framer-motion";
import { Info, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/campaign";
import { track } from "@/lib/track";
import { MaskedLine } from "./Reveal";
import { HeroQuote } from "./Reviews";

const StorefrontImage = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    className="relative"
  >
    <div className="absolute -inset-2 border border-[var(--brand-gold)] opacity-50" aria-hidden="true" />
    <picture>
      <source media="(max-width: 640px)" srcSet="/images/storefront-800.webp" />
      <img
        data-testid="hero-storefront-photo"
        src="/images/storefront.webp"
        alt="Entrance to All-In Precision Wellness in Easton, Pennsylvania."
        className="relative w-full h-64 sm:h-80 lg:h-[420px] object-cover object-[70%_center] shadow-lg"
      />
    </picture>
    <div className="absolute -bottom-6 left-4 sm:left-6 flex items-center gap-3 bg-white border border-[var(--brand-gold-soft)] shadow-md px-3 py-2">
      <img
        data-testid="hero-doctor-photo"
        src="/images/dr-crosson.png"
        alt="Dr. Jill Crosson, DO, MBA"
        className="h-14 w-14 sm:h-16 sm:w-16 rounded-full"
      />
      <div>
        <p className="font-serif-brand font-semibold leading-tight text-[var(--brand-navy)]">
          Dr. Jill Crosson, DO, MBA
        </p>
        <p className="text-xs text-[var(--brand-muted)]">Board-certified physician &bull; Easton, PA</p>
      </div>
    </div>
  </motion.div>
);

export const Hero = ({ variant, onStartGuide, onHsaInfo }) => {
  const callFirst = variant === "menopause-call";
  // Ads can link /menopause?focus=hrt to swap only the headline for
  // bioidentical/HRT keywords (doc: "Perimenopause anchor + headline variant").
  const hrtFocus =
    !callFirst && new URLSearchParams(window.location.search).get("focus") === "hrt";
  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-14 sm:pt-16 sm:pb-20 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
        <div className="text-left">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow"
            className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-gold)] mb-4"
          >
            Menopause &amp; Perimenopause Care &bull; Easton, PA
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="font-serif-brand text-4xl sm:text-5xl lg:text-[2.9rem] font-semibold tracking-tight leading-[1.12] text-[var(--brand-navy)]"
          >
            {hrtFocus ? (
              <>
                <MaskedLine delay={0.1}>{"Curious about\u00A0"}</MaskedLine>
                <MaskedLine delay={0.25}>
                  <span className="italic text-[var(--brand-gold)]">bioidentical hormones?</span>
                </MaskedLine>
              </>
            ) : (
              <>
                <MaskedLine delay={0.1}>Not feeling like yourself?</MaskedLine>
                <MaskedLine delay={0.25}>
                  Let&rsquo;s make time to <span className="italic text-[var(--brand-gold)]">understand why.</span>
                </MaskedLine>
              </>
            )}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            data-testid="hero-subheadline"
            className="mt-6 text-lg leading-relaxed text-[var(--brand-navy-deep)] max-w-xl"
          >
            {hrtFocus ? (
              <>
                Let&rsquo;s talk through your options &mdash; spend a full hour with Dr. Jill
                Crosson, DO, reviewing your health history, your symptoms, and whether hormone
                therapy is an option worth exploring for you.
              </>
            ) : (
              <>
                Hot flashes, disrupted sleep, brain fog, or changes in your energy and weight can leave
                you with a lot of questions. Spend a full hour with Dr. Jill Crosson reviewing your
                health history, talking through your concerns, and discussing your next steps.
              </>
            )}
          </motion.p>
          {!callFirst && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-5 flex items-center gap-2.5"
            >
              <p
                data-testid="hero-offer-line"
                className="inline-block border border-[var(--brand-gold)] bg-white px-4 py-2 text-base font-semibold text-[var(--brand-navy)]"
              >
                60-minute first visit &bull; $400 &bull; Private pay
              </p>
              {onHsaInfo && (
                <button
                  type="button"
                  data-testid="hero-hsa-info-button"
                  onClick={onHsaInfo}
                  aria-label="Did you know? HSA and FSA information"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--brand-gold)] bg-white text-[var(--brand-gold)] transition-colors hover:bg-[var(--brand-gold)] hover:text-white"
                >
                  <Info className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </motion.div>
          )}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-7"
          >
            {callFirst ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <a
                  data-testid="hero-call-button"
                  href={PHONE_TEL} data-call-location="hero"
                  onClick={() => track(variant, "call_click")}
                  className="inline-flex items-center justify-center gap-2 bg-[var(--brand-navy)] text-white text-lg font-semibold px-8 py-4 hover:bg-[var(--brand-navy-deep)] transition-colors"
                >
                  <Phone className="h-5 w-5" aria-hidden="true" /> Call {PHONE_DISPLAY}
                </a>
                <button
                  data-testid="hero-find-next-step-button"
                  onClick={onStartGuide}
                  className="inline-flex items-center justify-center border-2 border-[var(--brand-navy)] text-[var(--brand-navy)] text-lg font-semibold px-8 py-[14px] bg-white hover:bg-[var(--brand-cream)] transition-colors"
                >
                  Find My Next Step
                </button>
              </div>
            ) : (
              <button
                data-testid="hero-find-next-step-button"
                onClick={onStartGuide}
                className="inline-flex items-center justify-center bg-[var(--brand-navy)] text-white text-lg font-semibold px-10 py-4 hover:bg-[var(--brand-navy-deep)] transition-colors"
              >
                Find My Next Step
              </button>
            )}
            <p className="mt-3 text-sm text-[var(--brand-muted)]">
              A few simple questions about your visit. No medical diagnosis or commitment.
            </p>
            {!callFirst && (
              <a
                data-testid="hero-call-link"
                href={PHONE_TEL} data-call-location="hero"
                onClick={() => track(variant, "call_click")}
                className="mt-2 inline-block text-base font-medium text-[var(--brand-navy)] underline decoration-[var(--brand-gold)] decoration-2 underline-offset-4 hover:text-[var(--brand-gold)] transition-colors"
              >
                Prefer to talk? Call {PHONE_DISPLAY}.
              </a>
            )}
          </motion.div>
          <div className="hidden lg:block">
            <HeroQuote />
          </div>
        </div>
        <div className="pb-8">
          <StorefrontImage />
          <div className="lg:hidden mt-12">
            <HeroQuote />
          </div>
        </div>
      </div>
      <div className="heartbeat-divider max-w-6xl mx-auto" aria-hidden="true" />
    </section>
  );
};
